import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import {
  topicTrend, usageDistribution, accountExpansion, activityCalendar, adoptionFunnel,
  engagementBreadth, newVsReturningVolume, sourceBreakdown, churnRisk, retentionBySegment,
  companyOf,
} from '../../../../lib/adoptionAnalytics.js';
import { StackedArea, LorenzCurve, CalendarHeatmap, FunnelBars } from '../charts.jsx';
import { Kpi, Kpis } from '../Kpi.jsx';
import { PALETTE } from '../util.js';

const trendBadge = (t) => <span className={`badge-${t}`}>{t}</span>;

export default function Trends() {
  const { events, classMap, aliases } = useData();
  const [scope, setScope] = useState('__ALL__');
  const [topicDim, setTopicDim] = useState('area');

  const companies = useMemo(() => [...new Set(events.map((e) => companyOf(e.user, aliases)))].sort(), [events, aliases]);
  const ev = useMemo(() => (scope === '__ALL__' ? events : events.filter((e) => companyOf(e.user, aliases) === scope)), [events, aliases, scope]);

  const topic = useMemo(() => topicTrend(ev, classMap, { dim: topicDim }), [ev, classMap, topicDim]);
  const nvr = useMemo(() => newVsReturningVolume(ev), [ev]);
  const dist = useMemo(() => usageDistribution(ev), [ev]);
  const expansion = useMemo(() => accountExpansion(ev, { aliases }), [ev, aliases]);
  const cal = useMemo(() => activityCalendar(ev), [ev]);
  const funnel = useMemo(() => adoptionFunnel(ev), [ev]);
  const breadth = useMemo(() => engagementBreadth(ev, classMap, { aliases }), [ev, classMap, aliases]);
  const sources = useMemo(() => sourceBreakdown(ev), [ev]);
  const churn = useMemo(() => churnRisk(ev, { aliases }), [ev, aliases]);
  const seg = useMemo(() => retentionBySegment(ev, { aliases }), [ev, aliases]);
  const hasClass = ev.some((e) => classMap[e.text]);

  if (!ev.length) return <div className="status">No activity in this snapshot.</div>;

  const nvrSeries = [{ label: 'Returning users', data: nvr.returningVol }, { label: 'New users', data: nvr.newVol }];

  return (
    <>
      <div className="card-head">
        <h1>Trends &amp; distribution</h1>
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="__ALL__">All companies</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <section className="card">
        <div className="card-head">
          <h3>Topic trend over time</h3>
          <div className="toggle">
            <button className={topicDim === 'area' ? 'on' : ''} onClick={() => setTopicDim('area')}>Scientific area</button>
            <button className={topicDim === 'use' ? 'on' : ''} onClick={() => setTopicDim('use')}>Use case</button>
          </div>
        </div>
        {hasClass ? (
          <>
            <StackedArea weeks={topic.weeks} series={topic.series} />
            <div className="legend">{topic.series.map((s, i) => <span className="leg" key={s.label}><span className="sw" style={{ background: PALETTE[i % PALETTE.length] }} />{s.label}</span>)}</div>
          </>
        ) : <div className="status warn">Snapshot has no prompt classifications.</div>}
      </section>

      <section className="card">
        <h3>Interaction volume — new vs returning users</h3>
        <StackedArea weeks={nvr.weeks} series={nvrSeries} colors={['#5b8def', '#37c98b']} />
        <div className="legend">
          <span className="leg"><span className="sw" style={{ background: '#37c98b' }} />New users</span>
          <span className="leg"><span className="sw" style={{ background: '#5b8def' }} />Returning users</span>
        </div>
      </section>

      <div className="grid2">
        <section className="card">
          <h3>Usage distribution (inequality)</h3>
          <p className="desc">Lorenz curve — the further below the diagonal, the more usage is concentrated in a few users.</p>
          <LorenzCurve lorenz={dist.lorenz} gini={dist.gini} />
          <Kpis>
            <Kpi v={dist.gini} l="Gini coefficient" />
            <Kpi v={`${dist.top10Pct}%`} l="Top-10% users' share" />
            <Kpi v={dist.users} l="Users" />
          </Kpis>
        </section>
        <section className="card">
          <h3>Adoption funnel</h3>
          <p className="desc">How users progress from first query to sustained, habitual use.</p>
          <FunnelBars stages={funnel.stages} />
        </section>
      </div>

      <section className="card">
        <h3>Activity calendar — when usage happens (UTC)</h3>
        <CalendarHeatmap cal={cal} />
      </section>

      <section className="card">
        <h3>Account expansion (seat growth)</h3>
        <p className="desc">Cumulative distinct users (seats) and recent vs prior 4-week active-user momentum.</p>
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>Company</th><th className="r">Seats</th><th className="r">Active (recent 4wk)</th><th className="r">Prior 4wk</th><th>Trend</th></tr></thead>
            <tbody>
              {expansion.companies.map((c) => (
                <tr key={c.company}><td>{c.company}</td><td className="r">{c.seats}</td><td className="r">{c.recent}</td><td className="r">{c.prior}</td><td>{trendBadge(c.trend)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid2">
        <section className="card">
          <h3>Engagement breadth by account</h3>
          <p className="desc">Distinct scientific areas &amp; use cases each account touches.</p>
          <div className="table-wrap">
            <table className="tbl">
              <thead><tr><th>Company</th><th className="r">Areas</th><th className="r">Use cases</th><th className="r">Interactions</th></tr></thead>
              <tbody>
                {breadth.byAccount.map((a) => (
                  <tr key={a.company}><td>{a.company}</td><td className="r">{a.areas}</td><td className="r">{a.uses}</td><td className="r">{a.interactions}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="card">
          <h3>Usage by source (deployment / cell)</h3>
          <div className="hbars">
            {sources.map((s) => (
              <div className="hbar" key={s.source}>
                <div className="hbar-lbl" title={s.source}>{s.source}</div>
                <div className="hbar-track"><div className="hbar-fill" style={{ width: `${s.pct}%`, background: '#5b8def' }} /></div>
                <div className="hbar-n">{s.count} <span className="dim">({s.pct}%)</span></div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="card">
        <h3>Churn-risk leaderboard</h3>
        <p className="desc">Accounts ranked by recent vs prior 4-week interaction momentum. Negative = cooling off.</p>
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>Company</th><th className="r">Recent 4wk</th><th className="r">Prior 4wk</th><th className="r">Change</th><th className="r">Weeks idle</th></tr></thead>
            <tbody>
              {churn.map((c) => (
                <tr key={c.company}>
                  <td>{c.company}</td><td className="r">{c.recent}</td><td className="r">{c.prior}</td>
                  <td className="r" style={{ color: c.pctChange == null ? 'var(--mut)' : c.pctChange < 0 ? '#f3a6ae' : '#6fe0a6' }}>{c.pctChange == null ? 'new' : `${c.pctChange}%`}</td>
                  <td className="r">{c.weeksIdle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h3>Retention by segment</h3>
        <p className="desc">Average % of a segment's users still active N weeks after their first week.</p>
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>Segment</th><th className="r">Users</th>{seg.segments[0]?.curve.map((_, k) => <th className="r" key={k}>W{k}</th>)}</tr></thead>
            <tbody>
              {seg.segments.map((s) => (
                <tr key={s.name}><td>{s.name}</td><td className="r">{s.size}</td>{s.curve.map((v, k) => <td className="r" key={k}>{v == null ? '—' : `${v}%`}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
